import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertCircle, CheckCircle, Minus } from 'lucide-react';
import ActionTaskCard from './ActionTaskCard';

const MATURITY_CONFIG = {
    1: { label: 'Inceptive',  bg: 'bg-red-100',   text: 'text-red-700',   border: 'border-red-200'   },
    2: { label: 'Transient',  bg: 'bg-amber-100',  text: 'text-amber-700', border: 'border-amber-200' },
    3: { label: 'Stable',     bg: 'bg-blue-100',   text: 'text-blue-700',  border: 'border-blue-200'  },
    4: { label: 'Dynamic',    bg: 'bg-green-100',  text: 'text-green-700', border: 'border-green-200' },
};

/**
 * IndicatorActionSection — one indicator with its action task cards.
 *
 * Props:
 *   item          - { indicator_id, domain_id, subdomain_id, statement, plan: { recommendations, kpi }, error }
 *   defaultOpen   - auto-expand if level <= 2
 */
const IndicatorActionSection = ({ item, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);

    const level = item.average_level || item.current_level || 2;
    const maturity = MATURITY_CONFIG[Math.round(level)] || MATURITY_CONFIG[2];
    const recommendations = item.plan?.recommendations || [];
    const kpi = item.plan?.kpi || '';
    const hasError = !!item.error;

    if (!recommendations.length && !hasError) return null;

    const StatusIcon = level <= 1.5 ? AlertCircle : level >= 3.5 ? CheckCircle : Minus;
    const statusColor = level <= 1.5 ? 'text-red-400' : level >= 3.5 ? 'text-green-400' : 'text-amber-400';

    return (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            {/* Header row */}
            <button
                onClick={() => setOpen(p => !p)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer"
            >
                <StatusIcon className={`w-4 h-4 flex-shrink-0 ${statusColor}`} />
                <span className="text-xs font-bold text-[#D67C2C] bg-orange-50 px-2 py-0.5 rounded flex-shrink-0">
                    {item.indicator_id}
                </span>
                <p className="flex-1 text-sm text-gray-700 font-medium leading-snug truncate">
                    {item.statement || 'Indicator'}
                </p>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${maturity.bg} ${maturity.text} ${maturity.border}`}>
                        {maturity.label}
                    </span>
                    {recommendations.length > 0 && (
                        <span className="text-xs text-gray-400">{recommendations.length} action{recommendations.length > 1 ? 's' : ''}</span>
                    )}
                    {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
            </button>

            {/* Expanded content */}
            {open && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                    {hasError ? (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                            Could not generate plan for this indicator. Try regenerating.
                        </div>
                    ) : (
                        recommendations.map((rec, idx) => (
                            <ActionTaskCard
                                key={idx}
                                action={rec}
                                indicatorLevel={Math.round(level)}
                                kpi={kpi}
                                showKpi={idx === recommendations.length - 1}
                                index={idx}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default IndicatorActionSection;
